import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Form, Button, Row, Col, Tab, Nav, Alert, Spinner } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaCamera, FaCrop, FaSave, FaTimes, FaUpload, FaGraduationCap, FaBriefcase, FaBuilding } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { updateUser } from '../application/reducers/auth';
import toast from 'react-hot-toast';
import Cropper from 'react-cropper';
import 'react-cropper/node_modules/cropperjs/dist/cropper.min.css';
import ProfileAPI from '../services/ProfileAPI';
import ProfileImage from './ProfileImage';
import InstituteLogoImage from './InstituteLogoImage';
import './ProfileModal.scss';

const ProfileModal = ({ show, onHide }) => {
  // Debug logging
  console.log('ProfileModal render:', { show, onHide: !!onHide });

  // State management
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [isEditing, setIsEditing] = useState(true);

  // Image upload states
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [cropper, setCropper] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Institute logo upload states
  const [showLogoUpload, setShowLogoUpload] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoCropper, setLogoCropper] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);

  // Form data
  const [formData, setFormData] = useState({});

  // References
  const fileInputRef = useRef(null);
  const cropperRef = useRef(null);
  const logoFileInputRef = useRef(null);
  const logoCropperRef = useRef(null);

  // Redux state
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Tab configuration similar to StudentFormModal
  const tabs = [
    {
      key: 'basic',
      title: 'Basic Info',
      icon: <FaUser size={16} />
    },
    ...(profileData?.role_id === 3 ? [{
      key: 'student',
      title: 'Academic',
      icon: <FaGraduationCap size={16} />
    }] : []),
    ...(profileData?.role_id !== 3 && profileData?.role_id !== 1 ? [{
      key: 'employee',
      title: 'Professional',
      icon: <FaBriefcase size={16} />
    }] : []),
    ...(profileData?.role_id === 1 ? [{
      key: 'institute',
      title: 'Institute',
      icon: <FaBuilding size={16} />
    }] : [])
  ];

  // Load profile data on modal open
  useEffect(() => {
    if (show && user) {
      fetchProfileData();
    }
  }, [show, user]);

  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.keyCode === 27 && show) {
        onHide();
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset'; // Restore scrolling
    };
  }, [show, onHide]);

  // Fetch profile data from API
  const fetchProfileData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await ProfileAPI.getProfile();
      const result = response.data;

      if (result.success) {
        setProfileData(result.data);
        setFormData(result.data);
      } else {
        setError(result.message || 'Failed to fetch profile data');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (path, value) => {
    const pathArray = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < pathArray.length - 1; i++) {
        if (!current[pathArray[i]]) {
          current[pathArray[i]] = {};
        }
        current = current[pathArray[i]];
      }
      
      current[pathArray[pathArray.length - 1]] = value;
      return newData;
    });
  };

  // Save profile changes
  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await ProfileAPI.updateProfile(formData);
      const result = response.data;
      console.log(result);
      
      if (result.success) {
        // Show success toast
        toast.success(result.message || 'Profile updated successfully');
        
        // Update profile data
        setProfileData(result.data);
        
        // Update auth state with latest user data
        dispatch(updateUser({
          user: result.data,
          institute: result.data.institute,
          role: result.data.role
        }));
        
        // Close modal immediately
        onHide();
      } else {
        setError(result.message || 'Failed to update profile');
      }
    } catch (err) {
      setError('Network error occurred');
      console.error('Profile update error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel - reset form data and close modal
  const handleCancel = useCallback(() => {
    setFormData(profileData);
    setError('');
    setSuccess('');
    onHide();
  }, [profileData, onHide]);

  // Handle image file selection
  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result);
        setShowImageUpload(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle image crop and upload
  const handleImageUpload = async () => {
    if (!cropper) return;

    setUploading(true);
    setError('');

    try {
      // Get the cropped canvas directly like in the demo - this is more reliable
      const canvas = cropper.getCroppedCanvas({
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
        fillColor: '#fff' // White background for transparent areas
      });

      // Debug: Check if canvas is empty
      const ctx = canvas.getContext('2d');
      const imageDataCheck = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const isEmpty = imageDataCheck.data.every(pixel => pixel === 0);
      
      if (isEmpty) {
        console.error('Canvas is empty - crop coordinates might be invalid');
        setError('Invalid crop selection. Please try again.');
        return;
      }

      canvas.toBlob(async (blob) => {
        if (!blob || blob.size === 0) {
          setError('Failed to process image. Please try again.');
          return;
        }

        const formData = new FormData();
        formData.append('profile_image', blob, 'profile.jpg');

        const response = await ProfileAPI.uploadProfilePicture(formData);
        const result = response.data;

        if (result.success) {
          setSuccess('Profile picture updated successfully');
          setShowImageUpload(false);
          setSelectedImage(null);
          fetchProfileData(); // Refresh profile data
        } else {
          setError(result.message || 'Failed to upload image');
        }
      }, 'image/jpeg', 0.85);
    } catch (err) {
      setError('Failed to process image');
      console.error('Image upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  // Handle institute logo file selection
  const handleLogoSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedLogo(reader.result);
        setShowLogoUpload(true);
      };
      reader.readAsDataURL(file);
    }
  };

    // Handle institute logo crop and upload
  const handleLogoUpload = async () => {
    if (!logoCropper) return;

    setLogoUploading(true);
    setError('');

    try {
      // Get the cropped canvas directly like in the demo - this is more reliable
      const canvas = logoCropper.getCroppedCanvas({
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
        fillColor: '#fff' // White background for transparent areas
      });

      // Debug: Log canvas content to verify it's not empty
      const ctx = canvas.getContext('2d');
      const imageDataCheck = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const isEmpty = imageDataCheck.data.every(pixel => pixel === 0);
      
      if (isEmpty) {
        console.error('Canvas is empty - crop coordinates might be invalid');
        setError('Invalid crop selection. Please try again.');
        return;
      }

      canvas.toBlob(async (blob) => {
        if (!blob || blob.size === 0) {
          setError('Failed to process image. Please try again.');
          return;
        }

        const formData = new FormData();
        formData.append('institute_logo', blob, 'institute_logo.png');

        const response = await ProfileAPI.uploadInstituteLogo(formData);
        const result = response.data;

        if (result.success) {
          setSuccess('Institute logo updated successfully');
          setShowLogoUpload(false);
          setSelectedLogo(null);
          fetchProfileData(); // Refresh profile data
        } else {
          setError(result.message || 'Failed to upload logo');
        }
      }, 'image/png', 0.9);
    } catch (err) {
      setError('Failed to process logo');
      console.error('Logo upload error:', err);
    } finally {
      setLogoUploading(false);
    }
  };

  // Modal animation variants for right slide-in
  const modalVariants = {
    hidden: { 
      x: '100%',
      opacity: 0,
      transition: { duration: 0.3, ease: "easeInOut" }
    },
    visible: { 
      x: '0%',
      opacity: 1,
      transition: { duration: 0.3, ease: "easeInOut" }
    },
    exit: { 
      x: '100%',
      opacity: 0,
      transition: { duration: 0.3, ease: "easeInOut" }
    }
  };

  // Render basic information tab
  const renderBasicInfo = () => (
    <Row>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>First Name</Form.Label>
          <Form.Control
            type="text"
            value={formData.first_name || ''}
            onChange={(e) => handleInputChange('first_name', e.target.value)}
            disabled={!isEditing}
            className={isEditing ? 'editable-field' : ''}
            placeholder="Enter your first name"
          />
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Last Name</Form.Label>
          <Form.Control
            type="text"
            value={formData.last_name || ''}
            onChange={(e) => handleInputChange('last_name', e.target.value)}
            disabled={!isEditing}
            className={isEditing ? 'editable-field' : ''}
            placeholder="Enter your last name"
          />
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            value={formData.email || ''}
            onChange={(e) => handleInputChange('email', e.target.value)}
            disabled={!isEditing}
            className={isEditing ? 'editable-field' : ''}
            placeholder="Enter your email address"
          />
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Mobile</Form.Label>
          <Form.Control
            type="text"
            value={formData.mobile || ''}
            disabled
            className="readonly-field"
          />
          <Form.Text className="text-muted">
            Contact admin to change mobile number
          </Form.Text>
        </Form.Group>
      </Col>
    </Row>
  );

  // Render student-specific information
  const renderStudentInfo = () => {
    if (!profileData?.student_data) return null;

    const { class_section, parents } = profileData.student_data;

    return (
      <div>
        <h6 className="section-title">Personal Information</h6>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type="date"
                value={formData.student_data?.additional_info?.date_of_birth || ''}
                onChange={(e) => handleInputChange('additional_info.date_of_birth', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Blood Group</Form.Label>
              <Form.Control
                type="text"
                value={formData.student_data?.additional_info?.blood_group || ''}
                onChange={(e) => handleInputChange('additional_info.blood_group', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter blood group (e.g., A+, B-, O+)"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Religion</Form.Label>
              <Form.Control
                type="text"
                value={formData.student_data?.additional_info?.religion || ''}
                onChange={(e) => handleInputChange('additional_info.religion', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter your religion"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Nationality</Form.Label>
              <Form.Control
                type="text"
                value={formData.student_data?.additional_info?.nationality || ''}
                onChange={(e) => handleInputChange('additional_info.nationality', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter your nationality"
              />
            </Form.Group>
          </Col>
        </Row>

        <h6 className="section-title mt-4">Academic Information</h6>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Class</Form.Label>
              <Form.Control
                type="text"
                value={class_section?.class_name || 'N/A'}
                disabled
                className="readonly-field"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Section</Form.Label>
              <Form.Control
                type="text"
                value={class_section?.section_name || 'N/A'}
                disabled
                className="readonly-field"
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Roll Number</Form.Label>
              <Form.Control
                type="text"
                value={class_section?.roll_number || 'N/A'}
                disabled
                className="readonly-field"
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="facilities-section mt-4">
          <h6 className="section-title">Facilities</h6>
          <Row>
            <Col md={4}>
              <Form.Check
                type="checkbox"
                label="Transport Required"
                checked={formData.student_data?.academic_info?.transport_required || false}
                onChange={(e) => handleInputChange('academic_info.transport_required', e.target.checked)}
                disabled={!isEditing}
              />
            </Col>
            <Col md={4}>
              <Form.Check
                type="checkbox"
                label="Hostel Required"
                checked={formData.student_data?.academic_info?.hostel_required || false}
                onChange={(e) => handleInputChange('academic_info.hostel_required', e.target.checked)}
                disabled={!isEditing}
              />
            </Col>
            <Col md={4}>
              <Form.Check
                type="checkbox"
                label="Scholarship Applicable"
                checked={formData.student_data?.academic_info?.scholarship_applicable || false}
                onChange={(e) => handleInputChange('academic_info.scholarship_applicable', e.target.checked)}
                disabled={!isEditing}
              />
            </Col>
          </Row>
        </div>

        {parents && parents.length > 0 && (
          <div className="parents-section mt-4">
            <h6 className="section-title">Parent Information</h6>
            {parents.map((parent, index) => (
              <div key={index} className="parent-card mb-3">
                <Row>
                  <Col md={6}>
                    <strong>{parent.name}</strong>
                    <div className="text-muted">{parent.relationship}</div>
                  </Col>
                  <Col md={3}>
                    <small>Email: {parent.email || 'N/A'}</small>
                  </Col>
                  <Col md={3}>
                    <small>Mobile: {parent.mobile || 'N/A'}</small>
                  </Col>
                </Row>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render employee-specific information
  const renderEmployeeInfo = () => {
    if (!profileData?.employee_data) return null;

    const { employee_info } = profileData.employee_data;

    return (
      <div>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Employee Code</Form.Label>
              <Form.Control
                type="text"
                value={employee_info?.employee_code || 'N/A'}
                disabled
                className="readonly-field"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Designation</Form.Label>
              <Form.Control
                type="text"
                value={employee_info?.designation || 'N/A'}
                disabled
                className="readonly-field"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Department</Form.Label>
              <Form.Control
                type="text"
                value={employee_info?.department?.department_name || 'N/A'}
                disabled
                className="readonly-field"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date of Joining</Form.Label>
              <Form.Control
                type="date"
                value={employee_info?.date_of_joining || ''}
                disabled
                className="readonly-field"
              />
            </Form.Group>
          </Col>
        </Row>

        <h6 className="section-title mt-4">Personal Information</h6>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type="date"
                value={formData.employee_data?.employee_info?.date_of_birth || ''}
                onChange={(e) => handleInputChange('employee_info.date_of_birth', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Qualification</Form.Label>
              <Form.Control
                type="text"
                value={formData.employee_data?.employee_info?.qualification || ''}
                onChange={(e) => handleInputChange('employee_info.qualification', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter your highest qualification"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Experience</Form.Label>
              <Form.Control
                type="text"
                value={formData.employee_data?.employee_info?.experience || ''}
                onChange={(e) => handleInputChange('employee_info.experience', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter years of experience"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.employee_data?.employee_info?.address || ''}
                onChange={(e) => handleInputChange('employee_info.address', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter your current address"
              />
            </Form.Group>
          </Col>
        </Row>
      </div>
    );
  };

  // Render institute information for super admin (role_id 1)
  const renderInstituteInfo = () => {
    if (!profileData?.institute) return null;

    return (
      <div>
        
        {/* Institute Logo Section - Similar to Profile Image */}
        <div className="mb-4">
          <div className="d-flex align-items-center">
            <div className="institute-logo-container me-3" style={{ position: 'relative' }}>
              <InstituteLogoImage
                logo={formData.institute?.logo}
                className="institute-logo"
                style={{ 
                  height: '80px', 
                  width: 'auto', 
                  maxWidth: '120px',
                  objectFit: 'contain',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  padding: '4px'
                }}
              />
              {isEditing && (
                <motion.button
                  className="change-photo-btn"
                  style={{
                    position: 'absolute',
                    bottom: '-8px',
                    right: '-8px',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: '#007bff',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                  onClick={() => logoFileInputRef.current?.click()}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  disabled={logoUploading}
                >
                  {logoUploading ? (
                    <Spinner size="sm" animation="border" />
                  ) : (
                    <FaCamera size={14} />
                  )}
                </motion.button>
              )}
              
              {/* Upload Progress Overlay for Logo */}
              {logoUploading && (
                <div className="upload-progress-overlay" style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}>
                  <div className="upload-progress-content text-center">
                    <Spinner animation="border" size="sm" className="mb-2" variant="light" />
                    <small className="text-white">Uploading...</small>
                  </div>
                </div>
              )}
            </div>
            <div>
              <h6 className="mb-1">Institute Logo</h6>
              <small className="text-muted">
                {formData.institute?.logo ? 'Click the camera icon to change logo' : 'Click the camera icon to upload logo'}
              </small>
              <div className="mt-1">
                <small className="text-info">
                  <i className="fas fa-info-circle me-1"></i>
                  Logo will maintain aspect ratio with max height of 80px
                </small>
              </div>
            </div>
          </div>
        </div>
        
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Institute Name *</Form.Label>
              <Form.Control
                type="text"
                value={formData.institute?.institute_name || ''}
                onChange={(e) => handleInputChange('institute.institute_name', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter institute name"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Institute Code</Form.Label>
              <Form.Control
                type="text"
                value={formData.institute?.code || ''}
                onChange={(e) => handleInputChange('institute.code', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter institute code"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>School Code</Form.Label>
              <Form.Control
                type="text"
                value={formData.institute?.school_code || ''}
                onChange={(e) => handleInputChange('institute.school_code', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter school code"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Affiliation No</Form.Label>
              <Form.Control
                type="text"
                value={formData.institute?.affiliation_no || ''}
                onChange={(e) => handleInputChange('institute.affiliation_no', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter affiliation number"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                value={formData.institute?.phone || ''}
                onChange={(e) => handleInputChange('institute.phone', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter contact phone number"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>State</Form.Label>
              <Form.Control
                type="text"
                value={formData.institute?.state || ''}
                onChange={(e) => handleInputChange('institute.state', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter state"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Control
                type="text"
                value={formData.institute?.country || ''}
                onChange={(e) => handleInputChange('institute.country', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter country"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Pincode</Form.Label>
              <Form.Control
                type="text"
                value={formData.institute?.pincode || ''}
                onChange={(e) => handleInputChange('institute.pincode', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter pincode"
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.institute?.address || ''}
                onChange={(e) => handleInputChange('institute.address', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
                placeholder="Enter complete institute address"
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Timezone</Form.Label>
              <Form.Select
                value={formData.institute?.timezone || ''}
                onChange={(e) => handleInputChange('institute.timezone', e.target.value)}
                disabled={!isEditing}
                className={isEditing ? 'editable-field' : ''}
              >
                <option value="">Select Timezone</option>
                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                <option value="America/New_York">America/New_York (EST)</option>
                <option value="Europe/London">Europe/London (GMT)</option>
                <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Brand Display Settings Section */}
        <div className="mt-4">
          <h6 className="mb-3 text-primary">Brand Display Settings</h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Brand Display Type</Form.Label>
                <Form.Select
                  value={formData.institute?.brand_display_type || 'name'}
                  onChange={(e) => handleInputChange('institute.brand_display_type', e.target.value)}
                  disabled={!isEditing}
                  className={isEditing ? 'editable-field' : ''}
                >
                  <option value="name">Name Only</option>
                  <option value="logo">Logo Only</option>
                  <option value="both">Logo + Name</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  Choose how your brand appears in the navigation bar
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Custom Brand Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.institute?.brand_name || ''}
                  onChange={(e) => handleInputChange('institute.brand_name', e.target.value)}
                  disabled={!isEditing}
                  className={isEditing ? 'editable-field' : ''}
                  placeholder="Leave empty to use institute name"
                />
                <Form.Text className="text-muted">
                  Optional: Custom name to display in navbar (defaults to institute name)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Modal content rendered via createPortal */}
      {show && createPortal(
        <AnimatePresence>
          <div 
            className="modal-backdrop-custom"
            onClick={(e) => {
              console.log('Backdrop clicked');
              if (e.target === e.currentTarget) {
                handleCancel();
              }
            }}
          >
            <motion.div
              className="right-slide-modal profile-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="modal-header-custom profile-modal-header">
                <div className="d-flex align-items-center w-100">
                  <div className="profile-image-container me-3">
                    <ProfileImage
                      user={profileData}
                      size="medium"
                      roundedCircle
                      className="profile-image"
                      width="60"
                      height="60"
                    />
                    <motion.button
                      className="change-photo-btn"
                      style={{
                        position: 'absolute',
                        bottom: '-8px',
                        right: '-8px',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: '#007bff',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <Spinner size="sm" animation="border" />
                      ) : (
                        <FaCamera size={14} />
                      )}
                    </motion.button>
                    
                    {/* Upload Progress Overlay */}
                    {uploading && (
                      <div className="upload-progress-overlay" style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column'
                      }}>
                        <div className="upload-progress-content text-center">
                          <Spinner animation="border" size="sm" className="mb-2" variant="light" />
                          <small className="text-white">Uploading...</small>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <h4 className="modal-title-custom">
                      {profileData?.first_name} {profileData?.last_name}
                    </h4>
                    <small className="text-muted">
                      {profileData?.role?.role_name} • {profileData?.institute?.institute_name}
                    </small>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleCancel}
                    className="btn-close-custom"
                  >
                    <FaTimes size={20} />
                  </Button>
                </div>
              </div>

              {/* Scrollable Body with Tabs */}
              <div className="modal-body-scrollable">
                {/* Upload Progress Overlay for entire modal */}
                {(uploading || logoUploading) && (
                  <div className="upload-loading-overlay">
                    <div className="upload-loading-content">
                      <Spinner animation="border" variant="primary" className="mb-3" />
                      <h5 className="text-primary mb-2">
                        {uploading ? 'Uploading Profile Picture' : 'Uploading Institute Logo'}
                      </h5>
                      <p className="text-muted mb-0">Please wait while we process your image...</p>
                    </div>
                  </div>
                )}

                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError('')} className="mx-3 mt-3">
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mx-3 mt-3">
                    {success}
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" />
                    <div className="mt-2">Loading profile data...</div>
                  </div>
                ) : (
                  <Tab.Container
                    activeKey={activeTab}
                    onSelect={(key) => setActiveTab(key)}
                  >
                    {/* Tab Navigation - Clean Bootstrap Style */}
                    <div className="px-3 mb-4">
                      <Nav variant="tabs" className="justify-content-start border-bottom profile-nav-tabs">
                        {tabs.map((tab) => (
                          <Nav.Item key={tab.key}>
                            <Nav.Link
                              eventKey={tab.key}
                              className={`d-flex align-items-center px-3 py-2 ${
                                activeTab === tab.key ? 'active' : 'text-muted'
                              }`}
                            >
                              {tab.icon}
                              <span className="ms-2">{tab.title}</span>
                            </Nav.Link>
                          </Nav.Item>
                        ))}
                      </Nav>
                    </div>

                    {/* Tab Content */}
                    <div className="px-3">
                      <Tab.Content>
                        <Tab.Pane eventKey="basic">
                          <div className="form-section">

                            {renderBasicInfo()}
                          </div>
                        </Tab.Pane>

                        {profileData?.role_id === 3 && (
                          <Tab.Pane eventKey="student">
                            <div className="form-section">
                              {renderStudentInfo()}
                            </div>
                          </Tab.Pane>
                        )}

                        {profileData?.role_id !== 3 && profileData?.role_id !== 1 && (
                          <Tab.Pane eventKey="employee">
                            <div className="form-section">
                              {renderEmployeeInfo()}
                            </div>
                          </Tab.Pane>
                        )}

                        {profileData?.role_id === 1 && (
                          <Tab.Pane eventKey="institute">
                            <div className="form-section">
                              {renderInstituteInfo()}
                            </div>
                          </Tab.Pane>
                        )}
                      </Tab.Content>
                    </div>
                  </Tab.Container>
                )}
              </div>

              {/* Modal Footer */}
              <div className="modal-footer-custom profile-modal-footer">
                <div className="d-flex justify-content-between w-100">
                  <div>
                    {error && (
                      <small className="text-danger">
                        <i className="fas fa-exclamation-circle me-1"></i>
                        {error}
                      </small>
                    )}
                    {success && (
                      <small className="text-success">
                        <i className="fas fa-check-circle me-1"></i>
                        {success}
                      </small>
                    )}
                  </div>
                  <div className="footer-buttons">
                    <Button
                      variant="outline-secondary"
                      onClick={handleCancel}
                      disabled={saving}
                      className="me-2"
                    >
                      <FaTimes className="me-1" />
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSave}
                      disabled={saving || loading}
                    >
                      {saving ? (
                        <>
                          <Spinner size="sm" className="me-1" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaSave className="me-1" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </AnimatePresence>,
        document.body
      )}

      {/* Hidden file inputs */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleImageSelect}
      />
      
      <input
        type="file"
        ref={logoFileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleLogoSelect}
      />

      {/* Image Crop Modal */}
      <Modal
        show={showImageUpload}
        onHide={() => setShowImageUpload(false)}
        size="lg"
        centered
        className="image-crop-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaCrop className="me-2" />
            Crop Profile Picture
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedImage && (
            <div className="cropper-container">
              <Cropper
                ref={cropperRef}
                src={selectedImage}
                style={{ height: 400, width: '100%' }}
                aspectRatio={1}
                guides={true}
                viewMode={1}
                responsive={true}
                autoCropArea={1}
                checkOrientation={false}
                background={false}
                minCropBoxWidth={100}
                minCropBoxHeight={100}
                onInitialized={(instance) => setCropper(instance)}
                ready={() => {
                  // Ensure cropper is properly initialized
                  console.log('Profile cropper ready');
                }}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowImageUpload(false)}
            disabled={uploading}
            className="me-2"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleImageUpload}
            disabled={uploading || !cropper}
          >
            {uploading ? (
              <>
                <Spinner 
                  as="span"
                  animation="border"
                  size="sm" 
                  role="status" 
                  className="me-2" 
                />
                Uploading...
              </>
            ) : (
              <>
                <FaUpload className="me-1" />
                Upload Profile
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Institute Logo Crop Modal */}
      <Modal
        show={showLogoUpload}
        onHide={() => setShowLogoUpload(false)}
        size="lg"
        centered
        className="image-crop-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaCrop className="me-2" />
            Crop Institute Logo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedLogo && (
            <div className="cropper-container">
              <Cropper
                ref={logoCropperRef}
                src={selectedLogo}
                style={{ height: 400, width: '100%' }}
                aspectRatio={0}
                guides={true}
                viewMode={1}
                responsive={true}
                autoCropArea={1}
                checkOrientation={false}
                background={false}
                minCropBoxWidth={50}
                minCropBoxHeight={50}
                onInitialized={(instance) => setLogoCropper(instance)}
                ready={() => {
                  // Ensure cropper is properly initialized
                  console.log('Logo cropper ready');
                }}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowLogoUpload(false)}
            disabled={logoUploading}
            className="me-2"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleLogoUpload}
            disabled={logoUploading || !logoCropper}
          >
            {logoUploading ? (
              <>
                <Spinner 
                  as="span"
                  animation="border"
                  size="sm" 
                  role="status" 
                  className="me-2" 
                />
                Uploading...
              </>
            ) : (
              <>
                <FaUpload className="me-1" />
                Upload Logo
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProfileModal;
